import { createTheme } from '@mui/material/styles';
import { COLORS } from '../utils/Application_Constants';
import accordionStyles from './Accordion';
import buttonStyles from './Button';
import chipStyles from './Chip';

export default createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1920,
            // xl: 1536,
        },
    },
    typography: {
        fontFamily: ['"museo-sans"', 'sans-serif'].join(','),
        h3: {
            fontSize: '32px',
            fontWeight: '600',
            lineHeight: '40px',
        },
        h4: {
            fontWeight: '600',
        },
        h5: {
            fontWeight: '600',
            fontSize: '20px',
            lineHeight: '28px',
        },
        body1: {
            fontWeight: '600',
            fontSize: '16px',
            lineHeight: '24px',
        },
        body2: {
            fontWeight: '400',
            fontSize: '14px',
            lineHeight: '20px',
        },
    },
    spacing: 8,
    shape: {
        borderRadiusVariants: {
            sm: 4,
            md: 8,
            lg: 16,
            xl: 32,
            rounded: '50%',
        },
    },
    shadowsVariants: {
        sm: `0px 4px 2px 0px ${COLORS.LT_JOURNEY150}1F`,
        md: `0px 4px 2px 0px ${COLORS.LT_JOURNEY150}0D, 0px 16px 32px -4px ${COLORS.LT_JOURNEY150}1A`,
        lg: `0px 4px 2px 0px ${COLORS.LT_JOURNEY150}0D, 0px 24px 48px -8px ${COLORS.LT_JOURNEY150}1F`,
        xl: `0px 4px 2px 0px ${COLORS.LT_JOURNEY150}0D, 0px 56px 112px -20px ${COLORS.LT_JOURNEY150}2E`,
    },
    palette: {
        primary: { main: COLORS.LT_JOURNEY },
        secondary: { main: COLORS.MILANO_RED },
        grey: { main: COLORS.LT_MIDNIGHT75 },
        text: {
            primary: COLORS.LT_MIDNIGHT,
            secondary: COLORS.LT_STEEL,
        },
        divider: COLORS.LT_MIDNIGHT25,
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
        },
        MuiButton: buttonStyles,
        MuiIconButton: {
            variants: [
                {
                    props: { color: 'contained-primary' },
                    style: {
                        backgroundColor: COLORS.LT_JOURNEY,
                        color: 'white',
                        '&:active, &:focus, :focus-visible, :focus-within': {
                            backgroundColor: COLORS.LT_JOURNEY,
                            color: 'white',
                        },
                        '&:hover': {
                            backgroundColor: COLORS.LT_JOURNEY125,
                            color: 'white',
                        },
                        '&:disabled': {
                            backgroundColor: COLORS.LT_JOURNEY50,
                            color: 'white',
                        },
                    },
                },
            ],
        },
        MuiSelect: {
            styleOverrides: {
                ...{
                    root: {
                        backgroundColor: '#e0e0e0',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    color: COLORS.LT_MIDNIGHT,
                    fontFamily: 'museo-sans',
                    fontWeight: '500',
                    '&.Mui-focused ': {
                        borderColor: COLORS.LT_JOURNEY,
                    },
                    width: '100%',
                },
                input: {
                    color: COLORS.LT_MIDNIGHT,
                    background: 'white',
                },
                multiline: {
                    border: `1px solid ${COLORS.LT_MIDNIGHT5}`,
                    borderRadius: '8px',
                    padding: '16px 24px',
                },
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: COLORS.LT_MIDNIGHT,
                    fontSize: '16px',
                    fontWeight: '700',
                    marginLeft: '0px',
                    fontFamily: 'museo-sans',
                },
                formControl: {
                    color: COLORS.LT_MIDNIGHT,
                    transform: 'none',
                    position: 'inherit!important',
                },
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '0px',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'transparent',
                        transition: 'all 0.3s',
                    },
                    '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${COLORS.LT_JOURNEY}`,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${COLORS.LT_JOURNEY}`,
                    },

                    '&&& .MuiInputBase-input': {
                        padding: '12px',
                    },
                },
                multiline: {
                    padding: '0px',
                },
            },
        },

        MuiInput: {
            styleOverrides: {
                underline: {
                    '&:before': {
                        borderBottom: 'none',
                    },
                    '&:after': {
                        borderBottom: 'none',
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    width: '100%',
                    marginBottom: '1.5rem',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    padding: '6px 12px',
                    opacity: '0.7',
                    '&.Mui-selected ': {
                        opacity: '1',
                    },
                    '&.Mui-disabled ': {
                        opacity: '0.5',
                    },
                },
            },
        },
        MuiChip: chipStyles,

        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child td': {
                        borderBottom: 'none',
                    },
                },
            },
        },

        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${COLORS.LT_MIDNIGHT5}`,
                },
                body: {
                    color: COLORS.LT_MIDNIGHT75,
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    margin: '0 auto',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontWeight: 'normal',
                    '&.Mui-selected': { backgroundColor: '#00000014 !important' },
                    '&:hover .Mui-selected': {
                        backgroundColor: '#00000014 !important',
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                list: {
                    paddingTop: '16px',
                    paddingBottom: '16px',
                    minWidth: '150px',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                standardWarning: {
                    color: '#AD7829',
                    background: COLORS.LT_SUNFLOWER25,
                    '& .MuiAlert-icon': {
                        color: '#AD7829',
                    },
                },
                standardError: {
                    color: COLORS.LT_PEPPER,
                    background: COLORS.LT_PEPPER10,
                    '& .MuiAlert-icon': {
                        color: COLORS.LT_PEPPER,
                    },
                },
                standardInfo: {
                    color: COLORS.LT_JOURNEY,
                    background: COLORS.LT_JOURNEY10,
                    '& .MuiAlert-icon': {
                        color: COLORS.LT_JOURNEY,
                    },
                },
            },
        },
        MuiAccordion: accordionStyles,
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    borderRadius: '16px',
                    height: '7px',
                },
                colorPrimary: {
                    backgroundColor: COLORS.LT_JOURNEY10,
                    borderRadius: '16px',
                },
                barColorPrimary: {
                    backgroundColor: COLORS.LT_JOURNEY,
                    borderRadius: '16px',
                },
            },
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                        borderBottom: 'none !important',
                        fontSize: '14px',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        border: 'none',
                        marginBottom: '10px',
                        '[role="row"]': {
                            borderRadius: '8px',
                            backgroundColor: `${COLORS.LT_MIDNIGHT5}`,
                        },
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        whiteSpace: 'normal',
                        lineHeight: '1.2',
                        wordBreak: 'break-word',
                        textAlign: 'left',
                        fontWeight: 600,
                    },
                    '& .custom-header': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiDataGrid-virtualScrollerContent': {
                        borderTop: `1px solid ${COLORS.LT_MIDNIGHT10}`,
                    },
                    '& .MuiDataGrid-footerContainer': {
                        display: 'none',
                    },
                    '& .MuiDataGrid-row': {
                        minHeight: '48px !important',
                        maxHeight: '100% !important',
                        borderBottom: 'none !important',
                        '--DataGrid-rowBorderColor': 'transparent',
                    },
                    '& .MuiDataGrid-row.Mui-selected.even-row': {
                        backgroundColor: `${COLORS.WHITE}`,
                    },
                    '& .MuiDataGrid-row.Mui-selected.odd-row': {
                        backgroundColor: `${COLORS.LT_MIDNIGHT5} !important`,
                    },
                    '& .MuiDataGrid-row.Mui-selected.even-row:hover': {
                        backgroundColor: `${COLORS.WHITE}`,
                    },
                    '& .MuiDataGrid-row.Mui-selected.odd-row:hover': {
                        backgroundColor: `${COLORS.LT_MIDNIGHT5} !important`,
                    },
                    '& .MuiDataGrid-row.even-row:hover': {
                        backgroundColor: `${COLORS.WHITE}`,
                    },
                    '& .MuiDataGrid-row.odd-row:hover': {
                        backgroundColor: `${COLORS.LT_MIDNIGHT5}`,
                    },
                    '& .MuiDataGrid-virtualScrollerRenderZone': {
                        overflowX: 'hidden',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        overflowX: 'hidden',
                    },
                    '& .even-row': {
                        backgroundColor: `${COLORS.WHITE}`,
                    },
                    '& .odd-row': {
                        backgroundColor: `${COLORS.LT_MIDNIGHT5}`,
                    },
                    '& .group-even': {
                        backgroundColor: `${COLORS.WHITE}`,
                    },
                    '& .group-odd': {
                        backgroundColor: `${COLORS.WHITE} !important`,
                    },
                    '& .group-even .MuiDataGrid-cell, & .group-odd .MuiDataGrid-cell': {
                        borderBottom: 'none !important',
                    },
                    '& .child-date': {
                        paddingLeft: '14px',
                        fontSize: '12px',
                    },
                    '& .MuiDataGrid-columnHeader:focus': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-columnHeader:focus-visible': {
                        outline: `2px solid ${COLORS.LT_JOURNEY}`,
                    },
                    '& .bold-text': {
                        fontWeight: 'bold',
                    },
                    '&.striped .MuiDataGrid-row:nth-of-type(odd)': {
                        backgroundColor: `${COLORS.WHITE}`,
                    },
                    '&.striped .MuiDataGrid-row:nth-of-type(even)': {
                        backgroundColor: `${COLORS.LT_MIDNIGHT5}`,
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled.Mui-checked': {
                        color: COLORS.LT_JOURNEY50,
                    },
                },
            },
        },
    },
});
